import { uniqBy } from "lodash";
import { useMemo } from "react";
import { AttendanceTypes, SearchAndFilterTypes } from "src/constants";
import { useGlossary } from "src/hooks/useGlossary";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { useSchoolAdmin } from "src/hooks/useSchoolAdmin";
import * as GQL from "src/types/graphql";
import { GET_SCHOOLS_FOR_FILTERS } from "../../graphql/queries";
import { School } from "../../types";
import {
  CommonFilterProps,
  FormFilter,
  MultiSelectFilter,
} from "../CommonFilters";

type SchoolFilterProps = {
  formTemplateId: string;
} & CommonFilterProps;

export const ApplyingSchoolFilter = ({
  formTemplateId,
  filterSettings,
  ...props
}: SchoolFilterProps) => {
  const { applyingSchoolIds } = useSchoolAdmin();
  const { glossary } = useGlossary();

  const { remoteData: getSchoolsRemoteData } = useRemoteDataQuery<
    GQL.GetSchoolsForFilters,
    GQL.GetSchoolsForFiltersVariables
  >(GET_SCHOOLS_FOR_FILTERS, {
    skip: !formTemplateId,
    variables: {
      form_template_id: formTemplateId,
    },
  });

  const applyingSchoolOptions = useMemo(() => {
    if (!getSchoolsRemoteData.hasData()) return [];
    return formatSchoolOptions({
      schools: getSchoolsRemoteData.data.school,
      noSchoolLabel: glossary`No school`,
      idFilter:
        applyingSchoolIds.length &&
        filterSettings.getOne(SearchAndFilterTypes.Attendance) !==
          AttendanceTypes.Attending
          ? applyingSchoolIds
          : undefined,
    });
  }, [getSchoolsRemoteData, glossary, filterSettings, applyingSchoolIds]);

  return (
    <FormFilter label="Applying school">
      <MultiSelectFilter
        filterKey={SearchAndFilterTypes.School}
        options={applyingSchoolOptions}
        filterSettings={filterSettings}
        {...props}
        placeholder={glossary`Type a school`}
      />
    </FormFilter>
  );
};

export const AttendingSchoolFilter = ({
  formTemplateId,
  filterSettings,
  ...props
}: SchoolFilterProps) => {
  const { attendingSchoolIds } = useSchoolAdmin();
  const { glossary } = useGlossary();

  const { remoteData: getSchoolsRemoteData } = useRemoteDataQuery<
    GQL.GetSchoolsForFilters,
    GQL.GetSchoolsForFiltersVariables
  >(GET_SCHOOLS_FOR_FILTERS, {
    skip: !formTemplateId,
    variables: {
      form_template_id: formTemplateId,
    },
  });

  const attendingSchoolOptions = useMemo(() => {
    if (!getSchoolsRemoteData.hasData()) return [];
    return formatSchoolOptions({
      schools: getSchoolsRemoteData.data.attending_school,
      noSchoolLabel: glossary`No school`,
      idFilter:
        attendingSchoolIds.length &&
        filterSettings.getOne(SearchAndFilterTypes.Attendance) ===
          AttendanceTypes.Attending
          ? attendingSchoolIds
          : undefined,
    });
  }, [getSchoolsRemoteData, glossary, filterSettings, attendingSchoolIds]);

  return (
    <FormFilter label="Current school">
      <MultiSelectFilter
        filterKey={SearchAndFilterTypes.AttendingSchool}
        options={attendingSchoolOptions}
        filterSettings={filterSettings}
        {...props}
        placeholder={glossary`Type a school`}
      />
    </FormFilter>
  );
};

const formatSchoolOptions = ({
  schools,
  noSchoolLabel,
  idFilter,
}: {
  schools: School[];
  noSchoolLabel?: string;
  idFilter?: string[];
}) => {
  let schoolOptions = uniqBy(schools, "id")
    .map((school) => ({
      label: school.name,
      id: school.id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  if (idFilter) {
    schoolOptions = schoolOptions.filter((school) =>
      idFilter.includes(school.id)
    );
  } else if (noSchoolLabel) {
    schoolOptions.unshift({
      label: noSchoolLabel,
      id: "noschool",
    });
  }

  return schoolOptions;
};
